import { makeFixed, makeNumber, makeSigned } from '@/helpers/chartFormat'
import { direction, isoWeekLabel } from '@/helpers/insightsMetrics'

import { metricState, requestState, type InsightsMetricState } from './insightsState'
import { monthlyItem, monthlyRows, monthsIn, type ReviewsMonthlyWindow } from './mapReviewsAspects'
import type { ReviewsInsightsGrowthQuery, ReviewsInsightsLatestQuery } from '../../reviews.generated'

export interface ReviewsDistributionRow {
  stars: number
  value: number
}

export interface ReviewsDistribution {
  rows: ReviewsDistributionRow[]
  total: number
  state: InsightsMetricState
}

export type ReviewsTrendDirection = 'up' | 'down' | 'flat'

export interface ReviewsScoreTrend {
  value: string
  direction: ReviewsTrendDirection
}

export interface ReviewsScorePoint {
  value: string
  reviews: number
  trend?: ReviewsScoreTrend
}

export interface ReviewsScoreHistory {
  state: InsightsMetricState
  weeks: string[]
  ratings: number[]
  counts: number[]
  trend?: ReviewsScoreTrend
  points: ReviewsScorePoint[]
}

export interface ReviewsOwnInsightsView {
  score: {
    total: string
    totalCount: number
    rating: number | null
    ratingLabel: string
  }
  lifetimeDistribution: { rows: ReviewsDistributionRow[]; total: number }
}

const EMPTY_LABEL = '—'

const toShownTenths = (stars: number): number => Math.round(stars * 10)

type SummaryRow = {
  reviewCount?: number | null
  averageRecommendationRating?: number | null
  reviewsBad?: number | null
  reviewsWorse?: number | null
  reviewsGood?: number | null
  reviewsBetter?: number | null
  reviewsBest?: number | null
}

const DISTRIBUTION_BUCKETS = [
  ['reviewsBest', 5],
  ['reviewsBetter', 4],
  ['reviewsGood', 3],
  ['reviewsWorse', 2],
  ['reviewsBad', 1],
] as const

const LIFETIME_BUCKETS: { stars: number; pick: (row: SummaryRow) => number | null | undefined }[] = [
  { stars: 5, pick: (row) => row.reviewsBest },
  { stars: 4, pick: (row) => row.reviewsBetter },
  { stars: 3, pick: (row) => row.reviewsGood },
  { stars: 2, pick: (row) => row.reviewsWorse },
  { stars: 1, pick: (row) => row.reviewsBad },
]

export const mapReviewsDistribution = (
  growth: ReviewsInsightsGrowthQuery | null | undefined,
  window: ReviewsMonthlyWindow,
  failed: boolean,
): ReviewsDistribution => {
  const item = monthlyItem(growth, failed)
  const months = monthsIn(monthlyRows(item), window)
  const rows = DISTRIBUTION_BUCKETS.map(([bucket, stars]) => ({ stars, value: months.reduce((sum, row) => sum + (row[bucket] ?? 0), 0) }))
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  return { rows, total, state: metricState(item, total) }
}

export const mapReviewsOwnInsights = (
  latest: ReviewsInsightsLatestQuery | null | undefined,
  failed: boolean,
  { locale }: { locale: string },
): ReviewsOwnInsightsView => {
  const fixed = makeFixed(locale, 1)
  const number = makeNumber(locale)
  const [, , summaryItem] = failed ? [] : (latest?.manage?.insights?.results ?? [])
  const errored = requestState(summaryItem) === 'error'
  const row: SummaryRow | undefined =
    !errored && summaryItem?.response?.__typename === 'ManageInsightsRatingSummaryResponse' ? summaryItem.response.rows[0] : undefined
  const rating = row?.averageRecommendationRating ?? null
  const lifetimeRows = row && row.reviewsBest != null ? LIFETIME_BUCKETS.map(({ stars, pick }) => ({ stars, value: pick(row) ?? 0 })) : []

  return {
    score: {
      total: row ? number(row.reviewCount ?? 0) : EMPTY_LABEL,
      totalCount: row?.reviewCount ?? 0,
      rating,
      ratingLabel: rating == null ? EMPTY_LABEL : fixed(rating),
    },
    lifetimeDistribution: { rows: lifetimeRows, total: lifetimeRows.reduce((sum, item) => sum + item.value, 0) },
  }
}

export const mapReviewsScoreHistory = (
  growth: ReviewsInsightsGrowthQuery | null | undefined,
  failed: boolean,
  { locale }: { locale: string },
): ReviewsScoreHistory => {
  const fixed = makeFixed(locale, 1)
  const signed = makeSigned(locale, 1)
  const [, , historyItem] = failed ? [] : (growth?.manage?.insights?.results ?? [])
  const itemState = failed ? 'error' : requestState(historyItem)
  const rows =
    itemState === 'ready' && historyItem?.response?.__typename === 'ManageInsightsRatingHistoryResponse'
      ? historyItem.response.rows.flatMap(({ period, averageRecommendationRating: rating, reviewCount, ratingDeltaPrev }) =>
          rating == null ? [] : [{ period, rating, reviews: reviewCount ?? 0, ratingDeltaPrev }],
        )
      : []
  const trend = (delta: number | null | undefined): ReviewsScoreTrend | undefined => {
    if (delta == null) return undefined
    const shownDelta = toShownTenths(delta)
    return { value: signed(shownDelta / 10), direction: direction(shownDelta) }
  }
  const points = rows.map(({ rating, reviews, ratingDeltaPrev }) => ({ value: fixed(rating), reviews, trend: trend(ratingDeltaPrev) }))

  return {
    state: itemState === 'error' ? 'error' : rows.length > 0 ? 'ready' : 'empty',
    weeks: rows.map(({ period }) => isoWeekLabel(period)),
    ratings: rows.map(({ rating }) => rating),
    counts: rows.map(({ reviews }) => reviews),
    trend: points.at(-1)?.trend,
    points,
  }
}
